import { useLayoutEffect } from 'react';

interface LockElementArgs {
  element: HTMLElement | null;
  isLocked: boolean;
  overflowDataAttr: `data-${string}`;
  positionDataAttr: `data-${string}`;
  preventReflow: boolean;
  timestamp: number;
}

const lockElement = ({
  element,
  isLocked,
  overflowDataAttr,
  positionDataAttr,
  preventReflow,
  timestamp,
}: LockElementArgs) => {
  const dataDialogTimestamp = 'data-dialog-timestamp';

  const originalStyle = element ? window.getComputedStyle(element).overflowY : '';
  const originalStylePosition = element ? window.getComputedStyle(element).position : '';
  const existingOverflowAttr = element?.getAttribute(overflowDataAttr) ?? originalStyle;
  const existingPositionAttr = element?.getAttribute(positionDataAttr) ?? originalStylePosition;

  if (isLocked && element && !element.getAttribute(dataDialogTimestamp)) {
    // Set original overflow as data attribute
    element.setAttribute(overflowDataAttr,
      originalStyle);

    element.setAttribute(positionDataAttr,
      originalStylePosition);

    if (preventReflow && element.clientWidth !== element.offsetWidth) {
      element.style.overflowY = 'scroll';
      element.style.position = 'fixed';
    } else {
      element.style.overflowY = 'hidden';
    }
  }

  if (isLocked) {
    element?.setAttribute(dataDialogTimestamp,
      `${timestamp}`);
  }

  return () => {
    const exitingElement = element;

    // We check for the timestamp, so we can open a new dialog
    // as an old dialog is closing without breaking the scroll lock
    if (exitingElement && exitingElement.getAttribute(dataDialogTimestamp) === `${timestamp}`) {
      exitingElement.style.overflowY = existingOverflowAttr;
      exitingElement.style.position = existingPositionAttr;
      exitingElement.removeAttribute(dataDialogTimestamp);
      exitingElement.removeAttribute(overflowDataAttr);
      exitingElement.removeAttribute(positionDataAttr);
    }
  };
};

const lockElements = (args: LockElementArgs[]) =>
  args.map(lockElement);

export function useLockBodyScroll(args?: {
  elements?: (HTMLElement | null)[];
  isLocked: boolean;
}) {
  useLayoutEffect(() => {
    const timestamp = Date.now();
    const isLocked = args?.isLocked ?? true;

    const elementSelectorArgs: LockElementArgs[] = args?.elements?.map((element, index) => ({
      element,
      isLocked,
      overflowDataAttr: `data-${index}-overflow`,
      positionDataAttr: `data-${index}-position`,
      preventReflow: false,
      timestamp,
    })) ?? [];

    const unlockCallbacks = lockElements([
      {
        element: document.querySelector('body'),
        isLocked,
        overflowDataAttr: 'data-body-overflow',
        positionDataAttr: 'data-body-position',
        preventReflow: true,
        timestamp,
      },
      {
        element: document.querySelector('main'),
        isLocked,
        overflowDataAttr: 'data-main-overflow',
        positionDataAttr: 'data-main-position',
        preventReflow: true,
        timestamp,
      },
      ...(elementSelectorArgs),
    ]);

    // Re-enable scrolling when component unmounts
    return () => {
      unlockCallbacks.forEach(unlock => {
        unlock();
      });
    };
  },
  [args?.elements, args?.isLocked]); // Empty array ensures effect is only run on mount and unmount
}
